import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { settingsAPI } from '../lib/api';

export type WorkflowType = 'linkedin_enrichment' | 'instagram' | 'web_search_contact';

interface OrgSettingsWithOverrides {
  id?: string;
  organizationId?: string;
  settingsOverrides: Record<string, unknown> | null;
  [key: string]: unknown;
}

function extractPausedWorkflows(data: OrgSettingsWithOverrides | undefined): WorkflowType[] {
  if (!data) return [];
  const overrides = data.settingsOverrides || {};
  const paused = overrides.pausedWorkflows;
  if (!Array.isArray(paused)) return [];
  return paused as WorkflowType[];
}

export function useWorkflowSettings(organizationId: string | undefined) {
  const result = useQuery<OrgSettingsWithOverrides>({
    queryKey: ['orgSettings', organizationId],
    queryFn: async () => {
      const { data } = await settingsAPI.getOrgSettings(organizationId!);
      return data;
    },
    enabled: !!organizationId,
    staleTime: 30_000,
  });

  // Both memoized so consumers using these as useMemo/useEffect dependencies
  // don't see a new reference every render. Without this, workflows.tsx's
  // useMemo([..., isWorkflowPaused]) recomputed the workflows array every
  // render, which triggered useEffect([workflows]) → setSelectedWorkflow →
  // render → loop. extractPausedWorkflows returns a fresh [] when data is
  // absent, so memoizing the function alone is not enough — the array
  // identity must be stable too.
  const pausedWorkflows = useMemo(() => extractPausedWorkflows(result.data), [result.data]);

  const isWorkflowPaused = useCallback(
    (workflowType: WorkflowType): boolean => pausedWorkflows.includes(workflowType),
    [pausedWorkflows],
  );

  return {
    ...result,
    pausedWorkflows,
    isWorkflowPaused,
  };
}

export function useToggleWorkflowPause() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useAuth();

  return useMutation<
    OrgSettingsWithOverrides,
    Error,
    { workflowType: WorkflowType; paused: boolean }
  >({
    mutationFn: async ({ workflowType, paused }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) throw new Error('No organization selected');
      const { data } = await settingsAPI.updateOrgSettings(orgId, {
        workflowPause: { workflowType, paused },
      });
      return data;
    },
    onMutate: async ({ workflowType, paused }) => {
      const orgId = currentOrganization?.id;
      if (!orgId) return;

      const queryKey = ['orgSettings', orgId];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<OrgSettingsWithOverrides>(queryKey);

      queryClient.setQueryData<OrgSettingsWithOverrides>(queryKey, (old) => {
        if (!old) return old;
        const overrides = (old.settingsOverrides as Record<string, unknown>) || {};
        const current = Array.isArray(overrides.pausedWorkflows)
          ? (overrides.pausedWorkflows as string[])
          : [];

        const updated = paused
          ? Array.from(new Set([...current, workflowType]))
          : current.filter((t) => t !== workflowType);

        return {
          ...old,
          settingsOverrides: { ...overrides, pausedWorkflows: updated },
        };
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      const orgId = currentOrganization?.id;
      if (!orgId) return;
      const ctx = context as { previous?: OrgSettingsWithOverrides } | undefined;
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(['orgSettings', orgId], ctx.previous);
      }
    },
    onSettled: () => {
      const orgId = currentOrganization?.id;
      if (!orgId) return;
      queryClient.invalidateQueries({ queryKey: ['orgSettings', orgId] });
    },
  });
}
